/**
 * AuthButton — CTA primaire des écrans d'auth.
 *
 * @remarks
 * Reprend la grammaire du CTA de marque (`.btn-primary` du DS vitrine) :
 * dégradé indigo `--color-brand-light → --color-brand`, glow au survol, radius
 * net 8px (cf. SAAS-631). `loading` affiche le `Spinner` et désactive le bouton ;
 * `loadingLabel` remplace le libellé pendant le chargement.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from '@/shared/ui';
import { cn } from '@/lib/utils/cn';

const CTA =
  'mt-1 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[linear-gradient(180deg,var(--color-brand-light),var(--color-brand)_72%)] px-6 text-base font-medium text-white shadow-[0_10px_28px_-14px_rgba(124,92,255,0.6),inset_0_1px_0_rgba(255,255,255,0.16)] transition-all hover:-translate-y-px hover:shadow-[0_14px_34px_-12px_rgba(124,92,255,0.6),0_0_64px_-16px_rgba(124,92,255,0.45)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0';

export interface AuthButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  loadingLabel?: ReactNode;
  children: ReactNode;
}

export function AuthButton({
  loading = false,
  loadingLabel,
  children,
  className,
  disabled,
  type = 'submit',
  ...rest
}: AuthButtonProps) {
  return (
    <button type={type} disabled={disabled ?? loading} className={cn(CTA, className)} {...rest}>
      {loading ? (
        <>
          <Spinner size="sm" />
          {loadingLabel ?? children}
        </>
      ) : (
        children
      )}
    </button>
  );
}
